import { apyToApr } from "@stepfinance/common";
import type { NFTStakingPosition } from "@stepfinance/nft";
import type { FC } from "react";
import React, { useCallback, useContext, useMemo, useState } from "react";

import { useApricotStaking, useBonfidaStaking, useHubbleStaking, useRaydiumStaking } from "../hooks";
import { useQuarryPositionsQuery } from "../hooks/queries/farms/useQuarryPositionsQuery";
import type { StakingAPYMap } from "../hooks/queries/useStakingAPYQuery";
import { useStakingAPYQuery } from "../hooks/queries/useStakingAPYQuery";
import { useHedgeStaking } from "../hooks/staking/useHedgeStaking";
import { useNFTStaking } from "../hooks/staking/useNFTStaking";
import { useStarAtlasLockerStaking } from "../hooks/staking/useStarAtlasLockerStaking";
import { useUXDStaking } from "../hooks/staking/useUXDStaking";
import type { StakingPositions } from "../types/staking";
import { DUST_THRESHOLD, useHideDustConfig } from "./app/config";

type StakingContextState = {
  refreshNFTStaking: () => void;
  refreshing: boolean;
  setStakingBalance: (balance: number) => void;
  stakingBalance: number;
  stakingPositions: StakingPositions[];
  nftPositions: NFTStakingPosition[];
  totalPendingRewards: number;
  expected24HrRewards: number;
  totalValue: number;
  solStakingAPYMap: StakingAPYMap | undefined;
};

const StakingContext = React.createContext<null | StakingContextState>(null);

export const StakingContextProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [stakingBalance, setStakingBalance] = useState(0);

  const { data: quarryPositionsData } = useQuarryPositionsQuery();
  const raydiumStakingPositions = useRaydiumStaking();
  const bonfidaStaking = useBonfidaStaking();
  const apricotStaking = useApricotStaking();
  // const synthetifyStaking = useSynthetifyStaking();
  const hubbleStaking = useHubbleStaking();
  const UXDStakingPositions = useUXDStaking();
  const hedgeStakingPositions = useHedgeStaking();
  const starAtlasLockerPositions = useStarAtlasLockerStaking();
  const { nftPositions, refresh } = useNFTStaking();
  const { hideDust } = useHideDustConfig();
  const { data: solStakingAPYMap } = useStakingAPYQuery();

  const stakingPositions = useMemo(() => {
    const positionsArr: StakingPositions[] = [];

    const quarryPositions = quarryPositionsData?.stakingPositions ?? [];
    const positions = positionsArr.concat(
      raydiumStakingPositions,
      quarryPositions,
      bonfidaStaking,
      apricotStaking,
      hubbleStaking,
      UXDStakingPositions,
      hedgeStakingPositions,
      starAtlasLockerPositions,
    );
    if (hideDust) {
      return positions.filter((p) => {
        const pendingRewardUSD = p.pendingRewardUSD ?? 0;
        const posValue = (p.valueInUSD ?? 0) + pendingRewardUSD;
        return (posValue ?? 0) > DUST_THRESHOLD;
      });
    }
    return positions;
  }, [
    quarryPositionsData,
    raydiumStakingPositions,
    bonfidaStaking,
    apricotStaking,
    hubbleStaking,
    UXDStakingPositions,
    hedgeStakingPositions,
    starAtlasLockerPositions,
    hideDust,
  ]);

  const allPositions = useMemo(() => stakingPositions.concat(nftPositions), [stakingPositions, nftPositions]);

  const { totalValue, totalPendingRewards, expected24HrRewards } = useMemo(() => {
    const values = {
      totalValue: 0,
      totalPendingRewards: 0,
      expected24HrRewards: 0,
    };

    for (const position of allPositions) {
      const { apy, valueInUSD, pendingRewardUSD } = position;
      const apr = apy !== null ? apyToApr(apy * 100) : null;

      values.totalValue += (valueInUSD ?? 0) + (pendingRewardUSD ?? 0);
      values.totalPendingRewards += pendingRewardUSD ?? 0;
      values.expected24HrRewards += apr !== null ? (apr / 365 / 100) * (valueInUSD ?? 0) : 0;
    }

    return values;
  }, [allPositions]);

  const refreshNFTStaking = useCallback(() => {
    setRefreshing(true);
    refresh();
    setRefreshing(false);
  }, [refresh]);

  return (
    <StakingContext.Provider
      value={{
        refreshNFTStaking,
        refreshing,
        stakingPositions,
        nftPositions,
        totalPendingRewards,
        expected24HrRewards,
        setStakingBalance,
        stakingBalance,
        totalValue,
        solStakingAPYMap,
      }}
    >
      {children}
    </StakingContext.Provider>
  );
};

export function useStakingContext(): StakingContextState {
  const ctx = useContext(StakingContext);
  if (ctx === null) {
    throw new Error("Context not available");
  }
  return ctx;
}
